import { get, isEmpty } from 'lodash';
import {
  fetchBrandResourcesApi,
  fetchBrandConfigResourcesApi,
  fetchMasterDataApi,
  fetchOutletDetailsApi,
  fetchAppStartupInfoApi,
  fetchAppStartupInfoGetApi,
  fetchBrandCitiesApi,
  fetchSocialMediaCampaignDetailsApi,
  trackCustomerEngagementApi,
} from '../../api/api';
import {
  API_ERROR_RESPONSE,
  BEGIN_AJAX_CALL,
  DISPATCH_APPLICABLE_BRAND_MODULES,
  DISPATCH_BRAND_RESOURCES,
  DISPATCH_CONTEXTUAL_HOME,
  DISPATCH_SURFACE,
  DISPATCH_GUEST_LOCATION,
  DISPATCH_COMMUNITY_CONTEXT,
  END_AJAX_CALL,
  FETCH_APP_STARTUP_INFO,
  MERGE_APP_STARTUP_INFO,
  FETCH_BRAND_CITIES,
  FETCH_BRAND_RESOURCES,
  FETCH_MASTER_DATA,
  FETCH_OUTLET_DETAILS,
  DISPATCH_APP_RESOLUTION_CONTEXT,
  DISPATCH_GUEST_COUPON_AND_DEAL_DATA,
  DISPATCH_STATIC_CONTEXT,
  FETCH_SOCIAL_MEDIA_CAMPAIGN_DETAILS,
  CLEAR_SOCIAL_MEDIA_CAMPAIGN_DETAILS,
  TRACK_CUSTOMER_AGGREGATION,
  DISPATCH_LOGIN_REQUIRED_BEFORE_ORDERING,
  FETCH_NEARBY_OUTLET_DETAILS,
  DISPATCH_THEME_DATA,
  DISPATCH_ENTRY_CHANNEL,
  DISPATCH_ORDERING_AUTH_IN_PROGRESS,
  FETCH_BRAND_CONFIG_RESOURCE,
} from './actionType';

export const beginAjaxCallAction = () => async (dispatch) => {
  dispatch({
    type: BEGIN_AJAX_CALL,
    showSpinner: true,
  });
};

export const endAjaxCallAction = () => async (dispatch) => {
  dispatch({
    type: END_AJAX_CALL,
    showSpinner: false,
  });
};

export const apiErrorResponseAction = (errorResponse) => async (dispatch) => {
  dispatch({
    type: API_ERROR_RESPONSE,
    errorResponse,
  });
  dispatch({
    type: END_AJAX_CALL,
    showSpinner: false,
  });
};

export const clearApiErrorResponseAction = () => async (dispatch) => {
  dispatch({
    type: API_ERROR_RESPONSE,
    errorResponse: {},
  });
};

export const fetchMasterDataAction =
  ({ appName }) =>
  async (dispatch) => {
    dispatch({
      type: BEGIN_AJAX_CALL,
      showSpinner: true,
    });
    fetchMasterDataApi({ appName })
      .then((response) => {
        dispatch({
          type: FETCH_MASTER_DATA,
          masterData: get(response, 'data', {}),
        });
      })
      .catch((error) => {
        console.log(error);
      })
      .finally(() =>
        dispatch({
          type: END_AJAX_CALL,
          showSpinner: false,
        }),
      );
  };

export const fetchBrandResourcesAction =
  ({ brandId }) =>
  async (dispatch) => {
    const resourceResponse = await fetchBrandResourcesApi({ brandId })
      .then((response) => {
        dispatch({
          type: FETCH_BRAND_RESOURCES,
          resources: get(response, 'data.resource', {}),
        });
        return response;
      })
      .catch((error) => {
        console.log(error);
      });
    return resourceResponse;
  };

export const fetchBrandCitiesAction =
  ({ brandId }) =>
  async (dispatch) => {
    try {
      const citiesResponse = await fetchBrandCitiesApi({ brandId: brandId })
        .then((response) => {
          const cities = get(response, 'data.cities', []);
          dispatch({
            type: FETCH_BRAND_CITIES,
            brandCities: cities,
          });
          return response;
        })
        .catch((errorResponse) => {
          const responseData = {
            ...get(errorResponse, 'response.data', {}),
          };
          const responseStatus = get(errorResponse, 'response.status', '');
          if (responseStatus) {
            responseData['statusCode'] = responseStatus;
          }
          // dispatch status code in the errorResponse
          dispatch(apiErrorResponseAction(responseData));
          return get(errorResponse, 'response', {});
        });
      return citiesResponse;
    } catch (err) {
      console.error(err);
    }
  };

export const fetchOutletDetailsAction =
  ({ url }, withLoader = true) =>
  async (dispatch) => {
    try {
      withLoader && dispatch(beginAjaxCallAction());
      const outletDetailsResponse = await fetchOutletDetailsApi({ url })
        .then((response) => {
          const outletDetails = get(response, 'data', {});
          dispatch({
            type: FETCH_OUTLET_DETAILS,
            outletDetails,
          });
          return response;
        })
        .catch((errorResponse) => {
          const responseData = {
            ...get(errorResponse, 'response.data', {}),
          };
          const responseStatus = get(errorResponse, 'response.status', '');
          if (responseStatus) {
            responseData['statusCode'] = responseStatus;
          }
          // dispatch status code in the errorResponse
          dispatch(apiErrorResponseAction(responseData));
          return get(errorResponse, 'response', {});
        });
      return outletDetailsResponse;
    } catch (err) {
      console.error(err);
    } finally {
      withLoader && dispatch(endAjaxCallAction());
    }
  };

export const fetchOutletsAction =
  ({ url }, withLoader = true) =>
  async (dispatch) => {
    try {
      withLoader && dispatch(beginAjaxCallAction());
      const outletDetailsResponse = await fetchOutletDetailsApi({ url })
        .then((response) => {
          const outletDetails = get(response, 'data', {});
          return response;
        })
        .catch((errorResponse) => {
          const responseData = {
            ...get(errorResponse, 'response.data', {}),
          };
          const responseStatus = get(errorResponse, 'response.status', '');
          if (responseStatus) {
            responseData['statusCode'] = responseStatus;
          }
          // dispatch status code in the errorResponse
          dispatch(apiErrorResponseAction(responseData));
          return get(errorResponse, 'response', {});
        });
      return outletDetailsResponse;
    } catch (err) {
      console.error(err);
    } finally {
      withLoader && dispatch(endAjaxCallAction());
    }
  };

export const dispatchContextualHome = (contextualHome) => (dispatch) => {
  dispatch({
    type: DISPATCH_CONTEXTUAL_HOME,
    contextualHome,
  });
};

/** Live surface (site | app | ordering | legacy). Prefer resolveSurface() then this. */
export const dispatchSurface = (surface) => (dispatch) => {
  dispatch({
    type: DISPATCH_SURFACE,
    surface,
  });
};

export const dispatchStartupInfoAndResourceDataAction =
  (appStartupInfoData) => async (dispatch) => {
    const brandResources = get(appStartupInfoData, 'brandResource.resource', {});
    dispatch({
      type: MERGE_APP_STARTUP_INFO,
      appStartupInfo: appStartupInfoData,
    });
    if (!isEmpty(brandResources)) {
      dispatch({
        type: DISPATCH_BRAND_RESOURCES,
        brandResources,
      });
    }
  };

/**
 * Hydrate Redux from window.__FUDR_BOOT__ (prodServer inject).
 * Uses FETCH (replace) so cold boot matches a successful appStartup response.
 */
export const hydrateAppStartupFromBootAction =
  (appStartupInfoData) => async (dispatch) => {
    const brandResources = get(appStartupInfoData, 'brandResource.resource', {});
    dispatch({
      type: FETCH_APP_STARTUP_INFO,
      appStartupInfo: appStartupInfoData,
    });
    if (!isEmpty(brandResources)) {
      dispatch({
        type: DISPATCH_BRAND_RESOURCES,
        brandResources,
      });
    }
  };

export const fetchAppStartupInfoAction =
  ({ appStartupReqBody, appStartupInfo, useGet = true }, withLoader = true) =>
  async (dispatch) => {
    // appStartupInfo is optional. When provided (currently only by the Ordering shell),
    // the response is merged on top of the existing state so previously resolved fields
    // (e.g. brandId) are preserved even if this particular call's response omits them.
    // When omitted, the response replaces Redux state wholesale.
    //
    // Prefer GET /appStartup?include=... (edge-cacheable) when useGet is true; POST remains
    // available for callers that set useGet: false.
    try {
      withLoader && dispatch(beginAjaxCallAction());
      const include =
        get(appStartupReqBody, 'include') ||
        (get(appStartupReqBody, 'includeModules') || []).join(',');
      const startupRequest =
        useGet && appStartupReqBody
          ? fetchAppStartupInfoGetApi({
              subdomain: get(appStartupReqBody, 'subdomain'),
              include,
              brandId: get(appStartupReqBody, 'brandId'),
              findModules: get(appStartupReqBody, 'findModules', true),
            })
          : fetchAppStartupInfoApi({ appStartupReqBody });
      const appStartupInfoAPIRespone = await startupRequest
        .then((response) => {
          let appStartupInfoData = appStartupInfo;
          appStartupInfoData = { ...appStartupInfo, ...get(response, 'data', {}) };
          const brandResources = get(appStartupInfoData, 'brandResource.resource', {});
          dispatch({
            type: FETCH_APP_STARTUP_INFO,
            appStartupInfo: appStartupInfoData,
          });
          if (!isEmpty(brandResources)) {
            dispatch({
              type: DISPATCH_BRAND_RESOURCES,
              brandResources,
            });
          }
          return response;
        })
        .catch((errorResponse) => {
          const responseData = {
            ...get(errorResponse, 'response.data', {}),
          };
          const responseStatus = get(errorResponse, 'response.status', '');
          if (responseStatus) {
            responseData['statusCode'] = responseStatus;
          }
          // dispatch status code in the errorResponse
          dispatch(apiErrorResponseAction(responseData));
          return get(errorResponse, 'response', {});
        });
      return appStartupInfoAPIRespone;
    } catch (err) {
      console.error(err);
    } finally {
      withLoader && dispatch(endAjaxCallAction());
    }
  };

export const dispatchApplicableBrandModulesAction = (modules) => async (dispatch) => {
  dispatch({
    type: DISPATCH_APPLICABLE_BRAND_MODULES,
    modules,
  });
};

export const dispatchThemeDataAction = (themeData) => async (dispatch) => {
  dispatch({
    type: DISPATCH_THEME_DATA,
    themeData,
  });
};

export const dispatchGuestLocationAction =
  ({ guestLocation }) =>
  async (dispatch) => {
    try {
      dispatch({
        type: DISPATCH_GUEST_LOCATION,
        guestLocation,
      });
    } catch (err) {
      console.error(err);
    }
  };

export const dispatchCommunityContextAction = (communityContext) => (dispatch) => {
  dispatch({
    type: DISPATCH_COMMUNITY_CONTEXT,
    communityContext,
  });
};

export const dispatchAppResolutionContext = (context) => (dispatch) => {
  dispatch({
    type: DISPATCH_APP_RESOLUTION_CONTEXT,
    appResolutionContext: context,
  });
};

export const dispatchGuestCouponAndDealDataAction = (payload) => (dispatch) => {
  dispatch({
    type: DISPATCH_GUEST_COUPON_AND_DEAL_DATA,
    guestCouponAndDealData: payload,
  });
};

export const clearGuestCouponAndDealDataAction = () => (dispatch) => {
  dispatch({
    type: DISPATCH_GUEST_COUPON_AND_DEAL_DATA,
    guestCouponAndDealData: {},
  });
};

export const fetchNearbyOutletsAction =
  ({ url }) =>
  async (dispatch) => {
    try {
      const outletDetailsResponse = await fetchOutletDetailsApi({ url })
        .then((response) => {
          const nearbyOutlets = get(response, 'data', {});
          dispatch({
            type: FETCH_NEARBY_OUTLET_DETAILS,
            nearbyOutlets: nearbyOutlets,
          });
          return response;
        })
        .catch((errorResponse) => {
          return get(errorResponse, 'response', {});
        });
      return outletDetailsResponse;
    } catch (err) {
      console.error(err);
    }
  };

export const dispatchStaticContext = (context) => (dispatch) => {
  dispatch({
    type: DISPATCH_STATIC_CONTEXT,
    staticContext: context,
  });
};

export const fetchSocialMediaCampaignDetailsAction =
  ({ url }) =>
  async (dispatch) => {
    try {
      const socailMediaCampaignDetalsResponse = await fetchSocialMediaCampaignDetailsApi({ url })
        .then((response) => {
          const socialMediaCampaignDetails = get(response, 'data', {});
          dispatch({
            type: FETCH_SOCIAL_MEDIA_CAMPAIGN_DETAILS,
            socialMediaCampaignDetails,
          });
          return response;
        })
        .catch((errorResponse) => {
          return get(errorResponse, 'response', {});
        });
      return socailMediaCampaignDetalsResponse;
    } catch (err) {
      console.error(err);
    }
  };

export const clearSocialMediaCampaignDetailsAction = () => async (dispatch) => {
  dispatch({
    type: CLEAR_SOCIAL_MEDIA_CAMPAIGN_DETAILS,
    socialMediaCampaignDetails: {},
  });
};

export const trackCustomerEngagementAction =
  ({ trackingData }) =>
  async (dispatch) => {
    try {
      const customerAggregationResponse = await trackCustomerEngagementApi({ trackingData })
        .then((response) => {
          dispatch({
            type: TRACK_CUSTOMER_AGGREGATION,
            payload: response.data,
          });
          return response;
        })
        .catch((errorResponse) => {
          return get(errorResponse, 'response', {});
        });
      return customerAggregationResponse;
    } catch (err) {
      console.error(err);
    }
  };

export const dispatchEntryChannelAction = (entryChannel) => async (dispatch) => {
  dispatch({
    type: DISPATCH_ENTRY_CHANNEL,
    entryChannel,
  });
};

export const dispatchOrderingAuthInProgressAction = (orderingAuthInProgress) => async (dispatch) => {
  dispatch({
    type: DISPATCH_ORDERING_AUTH_IN_PROGRESS,
    orderingAuthInProgress: orderingAuthInProgress,
  });
};